import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, Home as HomeIcon, Calendar, Package, Users, CreditCard, Trash2, Check } from "lucide-react";

const CATS = [
  ["banquet_hall", "Banquet Hall"],
  ["lawn", "Lawn"],
  ["catering", "Catering"],
  ["hotel_banquet", "Hotel Banquet"],
  ["decoration", "Decoration"],
];

export default function OwnerDashboard() {
  const { user } = useAuth();
  const [tab, setTab] = useState("venues");
  const [venues, setVenues] = useState([]);
  const [subs, setSubs] = useState([]);
  const [pricing, setPricing] = useState({});
  const [inquiries, setInquiries] = useState([]);
  const [openAdd, setOpenAdd] = useState(false);
  const [selectedVenue, setSelectedVenue] = useState(null);

  const load = () => {
    api.get("/my/venues").then(r=>setVenues(r.data));
    api.get("/subscriptions").then(r=>setSubs(r.data));
    api.get("/config/pricing").then(r=>setPricing(r.data));
    api.get("/inquiries").then(r=>setInquiries(r.data));
  };
  useEffect(()=>{ load(); }, []);

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-10">
      <div className="flex flex-wrap items-end justify-between mb-6 gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-widest-2 text-[color:var(--wine)]">Venue owner</div>
          <h1 className="font-display text-5xl">Hello, {user?.name || "Owner"}</h1>
          <p className="text-sm text-neutral-500 mt-1">Manage your listings, availability & subscriptions.</p>
        </div>
        <button onClick={()=>setOpenAdd(true)} className="btn-wine" data-testid="add-venue-btn"><Plus className="w-4 h-4"/> Add venue</button>
      </div>

      <div className="flex gap-1 border-b border-[color:var(--line)] mb-6 overflow-x-auto">
        {[["venues","My venues",HomeIcon],["calendar","Availability",Calendar],["inquiries","Inquiries",Users],["subscription","Subscription",CreditCard]].map(([k,l,I])=>(
          <button key={k} onClick={()=>setTab(k)} className={`px-4 py-2.5 text-sm inline-flex items-center gap-2 ${tab===k ? "text-[color:var(--wine)] border-b-2 border-[color:var(--wine)]" : "text-neutral-500"}`} data-testid={`owner-tab-${k}`}>
            <I className="w-4 h-4"/> {l}
          </button>
        ))}
      </div>

      {tab==="venues" && (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {venues.map(v=>(
            <div key={v.id} className="card-luxury overflow-hidden">
              <img src={v.images?.[0] || "https://images.unsplash.com/photo-1519741497674-611481863552?w=800"} className="h-40 w-full object-cover" alt={v.name}/>
              <div className="p-4">
                <div className="font-display text-xl">{v.name}</div>
                <div className="text-xs text-neutral-500">{v.city} · {v.category}</div>
                <div className="mt-2 flex gap-2 text-xs">
                  <span className={`px-2 py-0.5 rounded-full ${v.is_approved ? "bg-green-50 text-green-700" : "bg-amber-50 text-amber-700"}`}>{v.is_approved ? "Approved" : "Pending"}</span>
                  {v.highlighted && <span className="px-2 py-0.5 rounded-full bg-[color:var(--gold)] text-black">Premium</span>}
                </div>
                <button onClick={()=>{ setSelectedVenue(v); setTab("calendar"); }} className="mt-3 btn-ghost !py-1.5 !px-3 text-xs w-full">Manage availability</button>
              </div>
            </div>
          ))}
          {!venues.length && <div className="col-span-3 text-center py-16 text-neutral-500 border border-dashed rounded-2xl">No venues yet. Add your first venue to go live.</div>}
        </div>
      )}

      {tab==="calendar" && (
        <AvailabilityManager venues={venues} selected={selectedVenue} setSelected={setSelectedVenue}/>
      )}

      {tab==="inquiries" && (
        <div className="card-luxury p-4">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-widest-2 text-neutral-500 text-left">
              <tr><th className="p-2">Name</th><th>Phone</th><th>Date</th><th>Pax</th><th>Message</th></tr>
            </thead>
            <tbody>
              {inquiries.map(i=>(
                <tr key={i.id} className="border-t"><td className="p-2">{i.name}</td><td>{i.phone}</td><td>{i.event_date}</td><td>{i.pax}</td><td className="text-neutral-600">{i.message}</td></tr>
              ))}
              {!inquiries.length && <tr><td colSpan={5} className="text-center py-8 text-neutral-500">No inquiries yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab==="subscription" && (
        <SubscriptionPanel pricing={pricing} subs={subs} onDone={load}/>
      )}

      {openAdd && <AddVenueModal onClose={()=>setOpenAdd(false)} onSaved={()=>{ setOpenAdd(false); load(); }}/>}
    </div>
  );
}

function AddVenueModal({ onClose, onSaved }) {
  const [f, setF] = useState({
    name: "", category: "lawn", city: "", state: "", address: "", description: "",
    pax_min: 100, pax_max: 500, price_per_day: 100000, veg_options: true, non_veg_options: true,
    amenities: "", images: "", videos: "", contact_phone: "", contact_email: "",
  });
  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/venues", {
        ...f,
        pax_min: Number(f.pax_min), pax_max: Number(f.pax_max), price_per_day: Number(f.price_per_day),
        amenities: f.amenities.split(",").map(x=>x.trim()).filter(Boolean),
        images: f.images.split(",").map(x=>x.trim()).filter(Boolean),
        videos: f.videos.split(",").map(x=>x.trim()).filter(Boolean),
        packages: [], catering_menu: [], faqs: [],
      });
      toast.success("Venue created (pending approval)");
      onSaved();
    } catch (e) { toast.error("Failed to create"); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={e=>e.stopPropagation()} onSubmit={save} className="bg-white rounded-2xl p-6 w-full max-w-2xl max-h-[92vh] overflow-y-auto space-y-3" data-testid="add-venue-modal">
        <h2 className="font-display text-2xl">Add new venue</h2>
        <div className="grid md:grid-cols-2 gap-3">
          <input required placeholder="Venue name" value={f.name} onChange={e=>setF({...f,name:e.target.value})} className="px-3 py-2.5 rounded-lg border" data-testid="v-name"/>
          <select value={f.category} onChange={e=>setF({...f,category:e.target.value})} className="px-3 py-2.5 rounded-lg border">{CATS.map(([k,l])=><option key={k} value={k}>{l}</option>)}</select>
          <input required placeholder="City" value={f.city} onChange={e=>setF({...f,city:e.target.value})} className="px-3 py-2.5 rounded-lg border" data-testid="v-city"/>
          <input placeholder="State" value={f.state} onChange={e=>setF({...f,state:e.target.value})} className="px-3 py-2.5 rounded-lg border"/>
          <input required placeholder="Address" value={f.address} onChange={e=>setF({...f,address:e.target.value})} className="px-3 py-2.5 rounded-lg border md:col-span-2"/>
          <textarea required placeholder="Description" value={f.description} onChange={e=>setF({...f,description:e.target.value})} className="px-3 py-2.5 rounded-lg border md:col-span-2" rows={3}/>
          <input type="number" placeholder="Pax min" value={f.pax_min} onChange={e=>setF({...f,pax_min:e.target.value})} className="px-3 py-2.5 rounded-lg border"/>
          <input type="number" placeholder="Pax max" value={f.pax_max} onChange={e=>setF({...f,pax_max:e.target.value})} className="px-3 py-2.5 rounded-lg border"/>
          <input type="number" placeholder="Price per day (INR)" value={f.price_per_day} onChange={e=>setF({...f,price_per_day:e.target.value})} className="px-3 py-2.5 rounded-lg border md:col-span-2"/>
          <input placeholder="Amenities (comma separated)" value={f.amenities} onChange={e=>setF({...f,amenities:e.target.value})} className="px-3 py-2.5 rounded-lg border md:col-span-2"/>
          <input placeholder="Image URLs (comma separated)" value={f.images} onChange={e=>setF({...f,images:e.target.value})} className="px-3 py-2.5 rounded-lg border md:col-span-2"/>
          <input placeholder="YouTube embed URLs (comma separated)" value={f.videos} onChange={e=>setF({...f,videos:e.target.value})} className="px-3 py-2.5 rounded-lg border md:col-span-2"/>
          <input placeholder="Contact phone" value={f.contact_phone} onChange={e=>setF({...f,contact_phone:e.target.value})} className="px-3 py-2.5 rounded-lg border"/>
          <input placeholder="Contact email" value={f.contact_email} onChange={e=>setF({...f,contact_email:e.target.value})} className="px-3 py-2.5 rounded-lg border"/>
        </div>
        <div className="flex gap-2 justify-end pt-2">
          <button type="button" onClick={onClose} className="btn-ghost">Cancel</button>
          <button className="btn-wine" data-testid="v-save">Save venue</button>
        </div>
      </form>
    </div>
  );
}

function AvailabilityManager({ venues, selected, setSelected }) {
  const [bookings, setBookings] = useState([]);
  const [date, setDate] = useState("");
  const [slot, setSlot] = useState("full_day");

  useEffect(()=>{
    if (!selected && venues.length) setSelected(venues[0]);
  }, [venues]);
  useEffect(()=>{
    if (selected) api.get(`/venues/${selected.id}/bookings`).then(r=>setBookings(r.data));
  }, [selected]);

  const addBooking = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/venues/${selected.id}/bookings`, { venue_id: selected.id, date, slot });
      toast.success("Marked booked");
      setDate("");
      const r = await api.get(`/venues/${selected.id}/bookings`); setBookings(r.data);
    } catch { toast.error("Failed"); }
  };

  if (!selected) return <div className="text-neutral-500">Add a venue first.</div>;
  return (
    <div className="grid md:grid-cols-3 gap-6">
      <div className="card-luxury p-4">
        <div className="text-xs uppercase tracking-widest-2 text-neutral-500 mb-2">Select venue</div>
        <div className="space-y-2">
          {venues.map(v=>(
            <button key={v.id} onClick={()=>setSelected(v)} className={`w-full text-left px-3 py-2 rounded-lg text-sm ${selected?.id===v.id ? "bg-[color:var(--wine)] text-[color:var(--gold-2)]" : "hover:bg-[color:var(--ivory-2)]"}`}>{v.name}</button>
          ))}
        </div>
      </div>
      <div className="md:col-span-2 space-y-4">
        <form onSubmit={addBooking} className="card-luxury p-4 flex flex-wrap items-end gap-3">
          <div>
            <div className="text-xs uppercase tracking-widest-2 text-neutral-500 mb-1">Date</div>
            <input required type="date" value={date} onChange={e=>setDate(e.target.value)} className="px-3 py-2 rounded-lg border" data-testid="booking-date"/>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest-2 text-neutral-500 mb-1">Slot</div>
            <select value={slot} onChange={e=>setSlot(e.target.value)} className="px-3 py-2 rounded-lg border">
              <option value="full_day">Full day</option>
              <option value="morning">Morning</option>
              <option value="afternoon">Afternoon</option>
              <option value="evening">Evening</option>
            </select>
          </div>
          <button className="btn-wine" data-testid="add-booking-btn">Mark booked</button>
        </form>

        <div className="card-luxury p-4">
          <div className="text-sm font-medium mb-2">Booked dates ({bookings.length})</div>
          <div className="flex flex-wrap gap-2">
            {bookings.map((b, i)=>(
              <span key={i} className="text-xs px-3 py-1 rounded-full bg-red-50 text-red-700 border border-red-100">{b.date} · {b.slot}</span>
            ))}
            {!bookings.length && <div className="text-neutral-500 text-sm">No bookings yet.</div>}
          </div>
        </div>
      </div>
    </div>
  );
}

function SubscriptionPanel({ pricing, subs, onDone }) {
  const [plan, setPlan] = useState("banquet_hall");
  const [months, setMonths] = useState(6);

  const amount = pricing?.categories?.[plan]?.[months === 12 ? "12m" : "6m"] || 0;

  const pay = async () => {
    try {
      const { data } = await api.post("/subscriptions/checkout", { plan, months, amount });
      // MOCK: instantly verify without real Razorpay
      await api.post(`/subscriptions/verify?order_id=${data.order_id}&payment_id=mock_${Date.now()}`);
      toast.success("Payment successful (MOCKED). Subscription active.");
      onDone();
    } catch (e) { toast.error("Payment failed"); }
  };

  return (
    <div className="grid md:grid-cols-3 gap-6">
      <div className="md:col-span-2 card-luxury p-6">
        <div className="font-display text-2xl mb-4">Renew or start a subscription</div>
        <div className="grid md:grid-cols-2 gap-3 mb-4">
          <select value={plan} onChange={e=>setPlan(e.target.value)} className="px-3 py-2.5 rounded-lg border">
            {Object.keys(pricing?.categories || {}).map(k=><option key={k} value={k}>{k.replace("_"," ")}</option>)}
          </select>
          <select value={months} onChange={e=>setMonths(Number(e.target.value))} className="px-3 py-2.5 rounded-lg border">
            <option value={6}>6 months</option>
            <option value={12}>12 months (10% off)</option>
          </select>
        </div>
        <div className="flex items-baseline gap-2 mb-6">
          <span className="text-xs text-neutral-500">Total</span>
          <span className="font-display text-3xl text-[color:var(--wine)]">₹{Number(amount).toLocaleString("en-IN")}</span>
        </div>
        <div className="p-3 mb-4 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800">
          Razorpay integration is MOCKED. Click "Pay now" to instantly mark subscription paid.
        </div>
        <button onClick={pay} className="btn-wine" data-testid="pay-now-btn">Pay ₹{Number(amount).toLocaleString("en-IN")} via Razorpay</button>
      </div>
      <div className="card-luxury p-4">
        <div className="text-sm font-medium mb-3">History</div>
        <div className="space-y-2 text-sm">
          {subs.map(s=>(
            <div key={s.id} className="flex justify-between border-b py-2">
              <div>
                <div className="capitalize">{s.plan?.replace("_"," ")} · {s.months}m</div>
                <div className="text-xs text-neutral-500">₹{Number(s.amount).toLocaleString("en-IN")}</div>
              </div>
              <span className={`text-xs px-2 py-1 rounded-full h-fit ${s.status==="paid" ? "bg-green-50 text-green-700" : "bg-neutral-100"}`}>{s.status}</span>
            </div>
          ))}
          {!subs.length && <div className="text-neutral-500 text-sm">No subscriptions yet.</div>}
        </div>
      </div>
    </div>
  );
}
